import { carInvoiceTotals } from "@carsfinders/contracts";
import { useQuery } from "@tanstack/react-query";

import { t } from "../../../lib/i18n";
import {
  carInvoicePreviewQueryKey,
  clientInvoicesQueryKey,
  fetchCarInvoicePreview,
  fetchClientInvoices,
} from "../../finance/api";
import {
  InvoiceLinesReadOnly,
  InvoiceTotalsReadOnly,
  refusalText,
  type InvoiceTotalRow,
} from "../../finance/components/invoice-lines";
import type { Invoice } from "../../finance/types";
import {
  INVOICE_STATUS_TEXT,
  cn,
  formatChf,
  invoiceOwed,
  invoiceStatus,
  invoiceStatusKey,
} from "../../finance/utils";
import { type BidDetailResponse, dealPaymentStanding } from "../types";
import { formatShortDate } from "../utils";

interface BidPaymentSectionProps {
  userId: string;
  deal: NonNullable<BidDetailResponse["deal"]>;
  /** Opens «Виставити рахунок» — offered only while the deal is not invoiced. */
  onIssueInvoice: () => void;
}

/**
 * «Оплата» on a WON bid — the money side of the deal.
 *
 * - Deal `won` (nothing issued yet): the server's own car-invoice PREVIEW, read
 *   only, under the same react-query key the issue drawer uses, so opening the
 *   drawer reuses it; correcting lines happens in the drawer.
 * - Deal invoiced (issued / prepaid / paid and later / forfeited): the stored
 *   car invoice for this deal, from the client's invoice list. Status and the
 *   outstanding amount are the contract's own rules (`deriveInvoiceStatus`,
 *   `invoiceOutstandingChf`) through the finance utils — never re-derived.
 * - Any other deal state (e.g. `voided`): nothing — the caller renders no section.
 */
export function BidPaymentSection({
  userId,
  deal,
  onIssueInvoice,
}: BidPaymentSectionProps) {
  const standing = dealPaymentStanding(deal.state);
  if (!standing) return null;

  return (
    <section
      data-testid="bid-payment-section"
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5"
    >
      {standing === "uninvoiced" ? (
        <PreviewBody
          userId={userId}
          dealId={deal.id}
          onIssueInvoice={onIssueInvoice}
        />
      ) : (
        <InvoicedBody userId={userId} dealId={deal.id} />
      )}
    </section>
  );
}

function SectionTitle({ trailing }: { trailing?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
        {t("bids.dAdmin.detailPage.paymentSection.title")}
      </h2>
      {trailing}
    </div>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: "muted" | "danger";
  children: React.ReactNode;
}) {
  return tone === "danger" ? (
    <p
      role="alert"
      className="mt-3 rounded-cf-md border border-cf-danger bg-cf-danger-tint px-3.5 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
    >
      {children}
    </p>
  ) : (
    <p className="mt-3 text-[13px] leading-[1.5] text-cf-ink-muted">
      {children}
    </p>
  );
}

/** Deal `won`: what issuing would bill, and the button that issues it. */
function PreviewBody({
  userId,
  dealId,
  onIssueInvoice,
}: {
  userId: string;
  dealId: string;
  onIssueInvoice: () => void;
}) {
  // Same key, fn and retry policy as IssueInvoiceDrawer — one cache entry.
  const previewQuery = useQuery({
    queryKey: carInvoicePreviewQueryKey(dealId, userId),
    queryFn: () => fetchCarInvoicePreview(dealId, userId),
    retry: false,
  });
  const preview = previewQuery.data;
  // The drawer's own display rule for the same figures.
  const totals = preview
    ? carInvoiceTotals(preview.lines, preview.depositChf)
    : null;

  const rows: InvoiceTotalRow[] = totals
    ? [
        {
          id: "total",
          label: t("adminFinance.drawer.carTotal"),
          value: formatChf(totals.totalChf),
        },
        ...(totals.prepaymentChf > 0
          ? [
              {
                id: "prepayment",
                label: t("adminFinance.drawer.carPrepayment"),
                value: `−${formatChf(totals.prepaymentChf)}`,
                tone: "info" as const,
              },
            ]
          : []),
        {
          id: "due",
          label: t("adminFinance.drawer.toPay"),
          value: formatChf(totals.dueChf),
          strong: true,
        },
      ]
    : [];

  return (
    <>
      <SectionTitle
        trailing={
          <button
            type="button"
            data-testid="bid-issue-invoice"
            onClick={onIssueInvoice}
            className="h-10 shrink-0 rounded-cf-md bg-cf-brand px-4 text-[13px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
          >
            {t("bids.dAdmin.detailPage.issueInvoice")}
          </button>
        }
      />
      <p className="mt-1.5 text-[13px] text-cf-ink-muted">
        {t("bids.dAdmin.detailPage.paymentSection.previewHint")}
      </p>
      {previewQuery.isError ? (
        <Notice tone="danger">
          {refusalText(
            previewQuery.error,
            "adminFinance.drawer.refusal.previewFailed",
          )}
        </Notice>
      ) : !preview ? (
        <p className="mt-3 text-[13px] text-cf-ink-subtle" aria-busy="true">
          {t("adminFinance.drawer.previewLoading")}
        </p>
      ) : (
        <div className="mt-3" data-testid="bid-payment-preview">
          <InvoiceLinesReadOnly lines={preview.lines} />
          <InvoiceTotalsReadOnly rows={rows} />
        </div>
      )}
    </>
  );
}

/** Deal invoiced: the stored car invoice for this deal. */
function InvoicedBody({ userId, dealId }: { userId: string; dealId: string }) {
  const invoicesQuery = useQuery({
    queryKey: clientInvoicesQueryKey(userId),
    queryFn: () => fetchClientInvoices(userId),
  });
  const invoice = invoicesQuery.data?.find(
    (candidate) => candidate.dealId === dealId && candidate.kind === "car",
  );

  if (invoicesQuery.isLoading) {
    return (
      <>
        <SectionTitle />
        <p className="mt-3 text-[13px] text-cf-ink-subtle" aria-busy="true">
          {t("common.loading.data")}
        </p>
      </>
    );
  }
  if (invoicesQuery.isError) {
    return (
      <>
        <SectionTitle />
        <Notice tone="danger">
          {t("bids.dAdmin.detailPage.paymentSection.invoicesFailed")}
        </Notice>
      </>
    );
  }
  if (!invoice) {
    return (
      <>
        <SectionTitle />
        <Notice tone="muted">
          <span data-testid="bid-invoice-missing">
            {t("bids.dAdmin.detailPage.paymentSection.invoiceMissing")}
          </span>
        </Notice>
      </>
    );
  }
  return <InvoiceView invoice={invoice} />;
}

function InvoiceView({ invoice }: { invoice: Invoice }) {
  const status = invoiceStatus(invoice);
  const rows: InvoiceTotalRow[] = [
    {
      id: "total",
      label: t("adminFinance.drawer.carTotal"),
      value: formatChf(invoice.totalChf),
    },
    ...(invoice.prepaymentChf > 0
      ? [
          {
            id: "prepayment",
            label: t("bids.dAdmin.detailPage.paymentSection.prepaid"),
            value: `−${formatChf(invoice.prepaymentChf)}`,
            tone: "info" as const,
          },
        ]
      : []),
    ...(invoice.paidChf > 0
      ? [
          {
            id: "paid",
            label: t("bids.dAdmin.detailPage.paymentSection.paid"),
            value: `−${formatChf(invoice.paidChf)}`,
            tone: "success" as const,
          },
        ]
      : []),
    {
      id: "outstanding",
      label: t("adminFinance.drawer.successRemaining"),
      value: formatChf(invoiceOwed(invoice)),
      strong: true,
    },
  ];

  return (
    <div data-testid="bid-payment-invoice">
      <SectionTitle
        trailing={
          <span
            data-testid="bid-invoice-status"
            data-status={status}
            className={cn(
              "inline-flex rounded-cf-sm bg-cf-fill px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase",
              INVOICE_STATUS_TEXT[status],
            )}
          >
            {t(invoiceStatusKey(status))}
          </span>
        }
      />
      <dl className="mt-3 grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
        <Fact
          label={t("bids.dAdmin.detailPage.paymentSection.invoiceNo")}
          value={invoice.no}
        />
        <Fact
          label={t("bids.dAdmin.detailPage.paymentSection.issuedAt")}
          value={formatShortDate(invoice.issuedAt.toISOString())}
        />
        <Fact
          label={t("bids.dAdmin.detailPage.paymentSection.dueAt")}
          value={formatShortDate(invoice.dueAt.toISOString())}
        />
      </dl>
      <div className="mt-3">
        <InvoiceLinesReadOnly lines={invoice.lines} />
        <InvoiceTotalsReadOnly rows={rows} />
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
        {label}
      </dt>
      <dd className="mt-1 truncate font-mono font-medium">{value}</dd>
    </div>
  );
}
